<template>
  <div>
    <section class="max-w-4xl mx-auto sm:max-w-screen-xl mt-4 sm:mt-7">
      <div v-lazy-container="{ selector: 'img' }">
        <template v-if="loading">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <vetster-skeleton-tile
              v-for="index in 4"
              :key="index"
            />
          </div>
        </template>
        <template v-else-if="partners && partners.length">
          <ul class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <vetster-dynamic-city-tile
              v-for="partner in partners"
              :key="partner.uuid"
              :partner="partner"
              :full-review="true"
            />
          </ul>
          <div class="col-span-1 md:col-span-2 flex justify-end mt-4">
            <vetster-button
              v-cloak
              :type="null"
              size="xl"
              variant="white"
              :href="guidedSearchUrl"
              rel="nofollow"
            >
              <span>{{ $t('search.see-more') }}</span>
              <fa
                :icon="['fal', 'long-arrow-right']"
                class="fa-fw text-lg"
              />
            </vetster-button>
          </div>
        </template>
        <template v-else>
          <div class="col-span-1 md:col-span-3 flex flex-col items-center justify-center">
            <div v-if="city">
              {{ $t('blogpost.get-help-top-vets', { city: city.name }) }}
            </div>
            <vetster-button
              v-cloak
              type="a"
              class="mt-6"
              size="xl"
              variant="bubblegum"
              :href="guidedSearchUrl"
              rel="nofollow"
            >
              <fa
                class="fa-fw text-xl"
                :icon="['fas', 'search']"
              />
              <span>{{ $t('symptoms-and-conditions.book-an-online-vet') }}</span>
            </vetster-button>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { mapActions, mapGetters } from 'vuex';

import VetsterDynamicCityTile from '@/components/VetsterDynamicCityTile';
import VetsterSkeletonTile from '@/components/VetsterSkeletonTile';
import Designation from '@/enums/Designation';
import { isMarketSiteCountry } from '@/i18n';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import TrackSearches from '@/mixins/TrackSearches';

export default {
  name: 'VetsterDynamicGeoVetList',

  components: {
    VetsterDynamicCityTile,
    VetsterSkeletonTile,
  },

  mixins: [
    LocalizesRoutes,
    TrackSearches,
  ],

  props: {
    city: {
      type: Object,
      default: null,
    },
    language: {
      type: String,
      default: 'en',
    },
    speciesTypes: {
      type: Array,
      default: null,
    },
    searchType: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      loading: true,
      partners: [],
      searchLocation: {
        city: null,
        province: null,
        country: null,
      },
    };
  },

  // TODO: Remove Hard Code Overrides for City, Province, Country and uncomment date_from, time_from, time_to, currency
  computed: {
    ...mapGetters(['getSiteCurrency']),
    ...mapGetters('geolocation', ['getGeolocation']),
    searchPayload() {
      return {
        designation: Designation.Veterinarian,
        timezone: DateTime.local().zoneName,
        country: this.searchLocation.country,
        province: this.searchLocation.province,
        city: this.searchLocation.city,
        language: this.language,
        platform: 'WEB',
        // currency: this.getSiteCurrency,
        species_treated: this.speciesTypes?.map((type) => type.value),
        areas_of_interest: [],
        page: 1,
        per_page: 4,
        type: this.searchType,
      };
    },
  },

  async mounted() {
    this.trackSearchLoad(this.searchType);
    await this.setSearchLocation();
    await this.fetchPartners();
  },

  methods: {
    ...mapActions('geolocation', ['fetchGeolocation']),
    async setSearchLocation() {
      if (this.city) {
        this.searchLocation = {
          city: this.city.name,
          province: this.city.state_code.toUpperCase(),
          country: this.city.country_code.toUpperCase(),
        };
      } else {
        await this.fetchGeolocation();

        if (isMarketSiteCountry(this.getGeolocation.country?.toLowerCase())) {
          this.searchLocation = this.getGeolocation;
        }
      }
    },
    async fetchPartners() {
      await this.$api.post('p/search', this.searchPayload).then((response) => {
        this.partners = response.data.data;
        setTimeout(() => {
          this.loading = false;
        }, 500);
      }).catch(() => {
        this.loading = false;
      });
    },
  },
};
</script>
